import { useContext, useEffect, useState, useRef, Fragment } from "react";
import { Context } from "Context";
import Separator from "./Separator";
import Button from "./Button";
import appConfig from "config.json";
import { toggleBodyClass } from "utils";

const { focusableElements } = appConfig;
const escapeKey = 27;
const tabKey = 9;
const descriptionId = "modalDescriptionId";
const titleId = "modalTitleId";

const syndicatedModalClass = "syndicated-modal";
const syndicatedModalContainerClass = `${syndicatedModalClass}__container`;
const syndicatedModalTitleClass = `${syndicatedModalClass}__title`;
const syndicatedModalContentClass = `${syndicatedModalClass}__content`;
const syndicatedModalFooterClass = `${syndicatedModalClass}__footer`;

const handleTabKey =
	({ lastElement, firstElement }) =>
	(e) => {
		if (!e.shiftKey && document.activeElement === lastElement) {
			firstElement.focus();
			e.preventDefault();
			return;
		}

		if (e.shiftKey && document.activeElement === firstElement) {
			lastElement.focus();
			e.preventDefault();
		}
	};

const getClassName = (content) => {
	return content?.className ? ` ${content.className}` : "";
};

const renderContent = ({
	title,
	description,
	content,
	primaryButton,
	secondaryButton,
	href,
	target,
	primaryButtonAnalyticsLinkId,
	secondaryButtonAnalyticsLinkId,
	webLink: linkContent,
	bypassBasePath,
	handleClose,
	brand,
	handleContinue,
}) => {
	if (!linkContent) {
		linkContent = {};
	}
	if (href) {
		linkContent.href = href;
	}
	if (target) {
		linkContent.target = target;
	}
	if (primaryButton) {
		linkContent.title = primaryButton;
	}
	return (
		<Fragment>
			{title && (
				<Fragment>
					<div
						className={`${syndicatedModalTitleClass} ${syndicatedModalTitleClass}--${brand}`}
						id={titleId}
					>
						{title}
					</div>
				</Fragment>
			)}
			{description && (
				<div
					className={`${syndicatedModalContentClass} ${syndicatedModalContentClass}--${brand}`}
					dangerouslySetInnerHTML={{ __html: description }}
					id={descriptionId}
				/>
			)}
			{content && (
				<div
					className={`${syndicatedModalContentClass} ${syndicatedModalContentClass}--${brand}`}
				>
					{content}
				</div>
			)}
			{(primaryButton || secondaryButton) && (
				<Fragment>
					<Separator />
					<div
						className={`${syndicatedModalFooterClass} ${syndicatedModalFooterClass}--${brand}`}
					>
						{secondaryButton && (
							<Button
								onClick={handleClose}
								secondary={true}
								title={secondaryButton}
								analyticsLinkId={secondaryButtonAnalyticsLinkId}
							/>
						)}
						{primaryButton && (
							<Button
								link={!!href}
								linkContent={{ ...linkContent, bypassBasePath }}
								title={primaryButton}
								analyticsLinkId={primaryButtonAnalyticsLinkId}
								onClick={(e) => {
									handleContinue && handleContinue(e);
									handleClose();
								}}
							/>
						)}
					</div>
				</Fragment>
			)}
		</Fragment>
	);
};

const Modal = () => {
	const [focusableModalElements, setFocusableModalElements] = useState(null);
	const [firstElement, setFirstElement] = useState(null);
	const [lastElement, setLastElement] = useState(null);

	const modalRef = useRef(null);
	const {
		modal,
		setModal,
		config: { brand },
		setUpdatedLocation,
	} = useContext(Context);

	const handleClose = () => { 
		const focusElem = modal?.focusElem;
		const focusElemScrollY = modal?.focusElemScrollY;
		if (focusElem) {
			focusElem.focus();
			setTimeout(() => { window.scroll({
				top: focusElemScrollY > -1 ? focusElemScrollY : focusElem?.offsetTop,
				behavior: "smooth"
			})}, 100);
		}
		setModal(false);
	};

	useEffect(() => {
		if (focusableModalElements) {
			setFirstElement(focusableModalElements[0]);
			setLastElement(focusableModalElements[focusableModalElements.length - 1]);
		}
	}, [focusableModalElements]);

	const keyListener = (e) => {
		if (![escapeKey, tabKey].includes(e.keyCode)) return null;
		const listener =
			e.keyCode === tabKey
				? handleTabKey({ lastElement, firstElement })
				: handleClose;
		return listener(e);
	};

	useEffect(() => {
		if (modalRef.current) {
			setFocusableModalElements(
				modalRef.current.querySelectorAll(focusableElements)
			);
		}
		if (modal) {
			if (!modal.preventFocus) {
				modalRef.current.focus();
			}
			toggleBodyClass();
			setUpdatedLocation(window.location.href);
		}
		return () => {
			toggleBodyClass(false);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modal]);

	return modal ? (
		<div
			className={`${syndicatedModalClass} ${syndicatedModalClass}--${brand}${getClassName(
				modal
			)}${
				modal.customClassModifier
					? ` ${syndicatedModalClass}--${modal.customClassModifier}`
					: ""
			}`}
			role="dialog"
			aria-modal="true"
			aria-labelledby={modal.title ? titleId : undefined}
			aria-label={
				!modal.title && modal.accessibilityTitle
					? modal.accessibilityTitle
					: undefined
			}
			aria-describedby={modal.description ? descriptionId : undefined}
			ref={modalRef}
			tabIndex="-1"
			onKeyDown={keyListener}
		>
			<div
				className={`${syndicatedModalContainerClass} ${syndicatedModalContainerClass}--${brand}`}
			>
				{modal?.logo}
				{modal && renderContent({ ...modal, handleClose, brand })}
			</div>
		</div>
	) : null;
};

export default Modal;
